import { useState, useEffect } from 'react'
import { useAuth } from '../firebase/useAuth'
import { UserService } from '../../services/api/userService'
import type { UserProfile, UserPreferences, TradingExperience, Instrument } from '../../types/user'
import { toast } from 'react-hot-toast'

export const useUserProfile = () => {
  const { user, userProfile: authUserProfile, loading: authLoading, refreshUserProfile } = useAuth()
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Use profile from AuthContext instead of making duplicate API calls
  const userProfile = authUserProfile

  // Load preferences when user profile is available
  useEffect(() => {
    if (!user || !userProfile) {
      setUserPreferences(null)
      setLoading(authLoading) // Use AuthContext loading state
      return
    }

    const loadUserPreferences = async () => {
      // Set up timeout protection to prevent infinite loading
      const timeoutId = setTimeout(() => {
        setLoading(false)
        setError('Loading timeout - please refresh the page')
        toast.error('Loading timed out. Please refresh the page.')
      }, 8000) // 8 second timeout

      try {
        setLoading(true)
        setError(null)

        // Load preferences if onboarding is complete
        if (userProfile.onboardingComplete) {
          try {
            const preferences = await UserService.getUserPreferences(user.uid)
            setUserPreferences(preferences)
          } catch (prefError) {
            // Preferences might not exist yet, that's okay
            setUserPreferences(null)
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load user preferences'
        setError(errorMessage)
        toast.error(errorMessage)
      } finally {
        // Clear timeout and always set loading to false
        clearTimeout(timeoutId)
        setLoading(false)
      }
    }

    void loadUserPreferences()
  }, [user, userProfile])

  // Complete onboarding
  const completeOnboarding = async (onboardingData: {
    experience: TradingExperience
    selectedInstruments: Instrument[]
    emailConsent?: boolean
  }) => {
    if (!user) {throw new Error('No user logged in')}

    // Set up timeout protection
    const timeoutId = setTimeout(() => {
      setLoading(false)
      setError('Onboarding timeout - please try again')
      toast.error('Setup taking too long. Please try again.')
    }, 5000) // 5 second timeout for onboarding - more reasonable

    try {
      setLoading(true)
      setError(null)

      const result = await UserService.completeOnboarding(user.uid, onboardingData)

      // Update AuthContext profile and local preferences to prevent redirect loops
      await refreshUserProfile()
      setUserPreferences(result.preferences)

      // Don't show toast here - let the calling component handle success message
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete onboarding'
      setError(errorMessage)
      toast.error(errorMessage)
      throw err
    } finally {
      // Always clear timeout and reset loading state
      clearTimeout(timeoutId)
      setLoading(false)
    }
  }

  // Skip onboarding
  const skipOnboarding = async () => {
    if (!user) {throw new Error('No user logged in')}

    try {
      setLoading(true)
      setError(null)

      const result = await UserService.skipOnboarding(user.uid)

      // Update AuthContext profile and local preferences
      await refreshUserProfile()
      setUserPreferences(result.preferences)

      // Don't show toast here - let the calling component handle success message
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to skip onboarding'
      setError(errorMessage)
      toast.error(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Update profile
  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) {throw new Error('No user logged in')}

    try {
      setError(null)
      await UserService.updateUserProfile(user.uid, updates)

      // Update local state
      refreshUserProfile()
      
      toast.success('Profile updated successfully!')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile'
      setError(errorMessage)
      toast.error(errorMessage)
      throw err
    }
  }

  return {
    userProfile,
    userPreferences,
    loading,
    error,
    completeOnboarding,
    skipOnboarding,
    updateProfile,
    isOnboardingComplete: userProfile?.onboardingComplete ?? false
  }
}